declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext
  }
}

/**
 * @property timeSmoothing The blending of values with the previous readings. (0 to 1)
 * @property windowSize Audio sample size for the fft to analyse.
 * @property minDBFS dBFS equivalent to 0 RMSFS output
 * @property maxDBFS dBFS equivalent to 1 RMSFS output
 */
interface MediaAudioLUFSAnalyserOptions {
  timeSmoothing: number
  windowSize: number
  minDBFS: number
  maxDBFS: number
}

/**
 * An abstraction of RMS and LUFS audio level analysis.
 * Handles analysis, timed updates, prevention of duplicates
 * and provides a simple interface for starting, pausing,
 * restarting and stopping analysis.
 */
class MediaAudioLUFSAnalyser {
  /**
   * The current audio context API available in the user's browser.
   * @private
   */
  private static AudioContext = window.AudioContext || window.webkitAudioContext

  /**
   * Tracks existing instances of MediaAudioLUFSAnalyser.
   * Used to prevent duplicate analysis of the same audio track.
   * Prevents issues in mobile safari and saves performance.
   * @private
   */
  private static Instances: Record<
    string,
    { instance: MediaAudioLUFSAnalyser; usage: number }
  > = {}

  /**
   * References the instance actively analysing an audio track.
   * Can either be itself or another LUSAnalyser if a track is already being analysed.
   * @private
   */
  private instance!: MediaAudioLUFSAnalyser

  /**
   * The id of the audio track being analysed.
   * Used to keep track and prevent duplicate analysis;
   * @private
   */
  private id: string = ''

  /**
   * The current audio context.
   * @private
   */
  audioContext?: AudioContext

  /**
   * The currently active audio source node.
   * It passes the audio stream to the audio analyser node.
   * @private
   */
  private audioSource?: MediaStreamAudioSourceNode

  /**
   * The currently active analyser node.
   * Used to calculate the fft of the stream.
   * @private
   */
  private analyser?: AnalyserNode

  /**
   * The media stream to analyse
   * @private
   */
  private stream: MediaStream | undefined

  /**
   * The smoothing time constant to use on the result of the fft.
   * As seen in https://webaudio.github.io/web-audio-api/#smoothing-over-time
   * @private
   */
  private readonly timeSmoothing: number

  /**
   * The size of the fft Blackman window.
   * As seen in https://webaudio.github.io/web-audio-api/#blackman-window
   * @private
   */
  private readonly windowSize: number

  /**
   * The dBFS to consider min loudness. (0 RMS)
   * @private
   */
  private readonly minDBFS: number

  /**
   * The dBFS to consider max loudness. (1 RMS)
   * @private
   */
  private readonly maxDBFS: number

  /**
   * An 8-bit integer array to store the results of the fft frequency analysis.
   * @private
   */
  private freqAmps!: Uint8Array

  /**
   * Stored information for timed updates.
   * Used for restarting analysis after a pause.
   * @private
   */
  private currentIntervalUpdates: {
    callback: (value: number) => void
    milliseconds: number
  }[] = []

  /**
   * An array to store the ids of the currently active update timers set by  calling `updateEvery`.
   * @private
   */
  private runningIntervalUpdates: number[] = []

  /**
   * Used to track when the on focus event listener is active.
   * @private
   */
  private focusResetting = false

  /**
   * Constructor for the audio
   * @param stream The audio stream to analyse
   * @param options (:MediaAudioLUFSAnalyserOptions)
   */
  constructor(
    stream?: MediaStream,
    options: MediaAudioLUFSAnalyserOptions = {} as MediaAudioLUFSAnalyserOptions
  ) {
    const {
      timeSmoothing = 0.95,
      windowSize = 1024,
      minDBFS = -60,
      maxDBFS = -20,
    } = options

    this.stream = stream
    this.timeSmoothing = timeSmoothing
    this.windowSize = windowSize
    this.minDBFS = minDBFS
    this.maxDBFS = maxDBFS
    this.updateInstance()
    this.initAnalyser()
  }

  /**
   * Compare analysis options with another MediaAudioLUFSAnalyser instance.
   * @param lufsAnalyser the instance to compare with
   * @returns true if equal, otherwise false
   */
  optionsCompare = (lufsAnalyser: MediaAudioLUFSAnalyser): boolean => {
    return (
      this.timeSmoothing === lufsAnalyser.timeSmoothing &&
      this.windowSize === lufsAnalyser.windowSize &&
      this.minDBFS === lufsAnalyser.minDBFS &&
      this.maxDBFS === lufsAnalyser.maxDBFS
    )
  }

  /**
   * Updates the current referenced instance and the instances array.
   * Use after changing mediaSource.
   */
  updateInstance = (): void => {
    if (this.stream === undefined) {
      return
    }

    const track = this.stream.getAudioTracks()[0] ?? null

    if (track === null) {
      return
    }

    const { id } = track
    const instanceContainer = MediaAudioLUFSAnalyser.Instances[id]

    if (instanceContainer !== undefined) {
      const { instance } = instanceContainer

      if (instance.optionsCompare(this)) {
        this.instance = instance
        instanceContainer.usage += 1

        return
      }

      const ms = this.stream

      ms.removeTrack(track)
      ms.addTrack(track.clone())
    }

    MediaAudioLUFSAnalyser.Instances[id] = { instance: this, usage: 1 }
    this.instance = this
    this.id = id
  }

  /**
   * Remove this instance from the active instances array.
   * Use when stopping analysis after releasing resources.
   */
  clearFromInstances = (): void => {
    if (!this.instance) return
    const instanceInfo = MediaAudioLUFSAnalyser.Instances[this.instance.id]
    if (!instanceInfo) return
    if (instanceInfo.usage > 1) {
      instanceInfo.usage -= 1
    } else {
      delete MediaAudioLUFSAnalyser.Instances[this.instance.id]
    }
  }

  /**
   * Setup audio nodes and their configs, and allocate arrays.
   * @private
   */
  initAnalyser = (): void => {
    if (!this.instance || !this.instance.stream) return
    this.instance.audioContext ??= new MediaAudioLUFSAnalyser.AudioContext()
    this.instance.audioSource ??=
      this.instance.audioContext.createMediaStreamSource(this.instance.stream)
    if (!this.instance.analyser) {
      this.instance.analyser = this.instance.audioContext.createAnalyser()
      this.instance.analyser.fftSize = this.instance.windowSize
      this.instance.analyser.smoothingTimeConstant = this.instance.timeSmoothing
      this.instance.analyser.minDecibels = this.instance.minDBFS
      this.instance.analyser.maxDecibels = this.instance.maxDBFS
      this.instance.audioSource.connect(this.instance.analyser)
    }
    this.instance.freqAmps ??= new Uint8Array(
      this.instance.analyser.frequencyBinCount
    )
    if (
      this.instance.freqAmps.length !== this.instance.analyser.frequencyBinCount
    )
      this.instance.freqAmps = new Uint8Array(
        this.instance.analyser.frequencyBinCount
      )

    if (!this.focusResetting) {
      // prevents issue in safari where analysers stop working when tab blurred
      window.addEventListener('focus', this.reset)
      this.focusResetting = true
    }
  }

  /**
   * Just pausing and unpausing the analysis.
   * Used on window refocus to fix safari mobile issue.
   */
  reset = (): void => {
    this.pause()
    this.unpause()
  }

  /**
   * Change the current audio stream being analysed to another.
   * @param stream the new audio stream
   */
  setStream = (stream: MediaStream | undefined): void => {
    if (stream === this.stream) return

    this.clean()
    this.stream = stream
    this.updateInstance()
    this.initAnalyser()
    this.restartUpdates()
  }

  /**
   * Apply an fft to the latest <windowsSize> audio samples and calculate their root-mean-square loudness value
   * @returns current loudness value (0 to 1). -1 if analyser not initialized
   */
  getRMSValue = (): number => {
    if (!this.analyser) return -1
    this.analyser?.getByteFrequencyData(this.freqAmps) // getFloatFrequencyData returns the fft result in dbs
    const sumFreq_dBFS = this.freqAmps.reduce(
      (sum, freq_dBFS) => sum + freq_dBFS * freq_dBFS,
      0
    )
    const rms = Math.sqrt(sumFreq_dBFS / this.freqAmps.length)
    return 1.037 / (1 + Math.exp(4 - Math.log2(rms + 1))) - 0.018 // convert 0-255 log scale to 0-1 log sigmoid weighted scale
  }

  // getLUFValue(): number {
  //   return 0
  // }

  /**
   * Get a function to be called with the current RMS value every n milliseconds
   * @param callback the function to call with the updated RMS loudness value
   * @param milliseconds the time to wait between updates
   */
  updateEvery = (
    callback: (meterValue: number) => void,
    milliseconds: number
  ): void => {
    const id = window.setInterval(() => {
      callback(this.instance?.getRMSValue() ?? 0)
    }, milliseconds)

    this.currentIntervalUpdates.push({
      callback,
      milliseconds,
    })
    this.runningIntervalUpdates.push(id)
  }

  /**
   * Pause all queued timed updates.
   */
  pauseUpdates = (): void => {
    let intervalId: number | undefined = 1
    while (intervalId) {
      intervalId = this.runningIntervalUpdates.pop()
      window.clearInterval(intervalId)
    }
  }

  /**
   * Restart all timed updates.
   */
  restartUpdates = (): void => {
    this.currentIntervalUpdates.forEach((updateInfo) => {
      const id = window.setInterval(() => {
        updateInfo.callback(this.instance?.getRMSValue() ?? 0)
      }, updateInfo.milliseconds)

      this.runningIntervalUpdates.push(id)
    })
  }

  /**
   * Cancel and pause all timed updates.
   */
  cancelUpdates = (): void => {
    this.pauseUpdates()
    let updateInfo
    while (updateInfo) {
      this.currentIntervalUpdates.pop()
    }
  }

  /**
   * Cancel timed updates and disconnect audio nodes.
   */
  clean = (): void => {
    this.pauseUpdates()

    this.analyser?.disconnect()
    delete this.analyser

    this.audioSource?.disconnect()
    delete this.audioSource
  }

  /**
   * Cancel timed updates, clean audio nodes and release resources.
   */
  cleanAll = (): void => {
    this.clean()
    this.cancelUpdates()

    this.audioContext?.close().catch()
    delete this.audioContext

    window.removeEventListener('focus', this.reset)
    this.focusResetting = false

    this.clearFromInstances()
  }

  /**
   * Cancel timed updates and disconnect audio nodes.
   * Alias for clean()
   */
  pause = this.clean

  /**
   * Restart analysis and updates.
   */
  unpause = () => {
    this.initAnalyser()
    this.restartUpdates()
  }
}

export { MediaAudioLUFSAnalyser }
